import React, { useEffect, useState } from 'react';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { useAuth } from './Auth/AuthProvider';
import { useUISettings } from './Context/UISettingsProvider';
import './ProtectedRoute.css';
import { UserType } from './Types/User';
import LoadingIcon from './Components/LoadingIcon';

interface ProtectedRouteProps {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
  supportsAnonymous?: boolean;
  supportsNonPremium?: boolean;
  supportUserTypes?: UserType[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  ...rest
}) => {
  const {
    isAuthenticated,
    isUserAnonymous,
    user,
    userAuthorizationInfo,
    validateSession,
    getUserAuthorizationInfo,
  } = useAuth();
  const { allowAnonymous } = useUISettings();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [allow, setAllow] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('/signin');
  const [redirectBackAfterAuth, setRedirectBackAfterAuth] =
    useState<boolean>(true);

  useEffect(() => {
    // Wait for authorization info to be obtained
    if (user === undefined || userAuthorizationInfo === undefined) {
      setIsLoading(true);
      return;
    }

    console.log(`Checking auth for route ${rest.path}`, {
      user,
      userAuthorizationInfo,
    });
    let result = true;
    if (!user || isUserAnonymous) {
      if (!rest.supportsAnonymous || !allowAnonymous) {
        result = false; // Disallow anonymous or unauthenticated users if either the route or param don't allow
        setRedirectUrl('/signup');
        setRedirectBackAfterAuth(true);
        console.log(`Route ${rest.path} does not support anonymous users`);
      }
    } else if (
      rest.supportUserTypes &&
      !rest.supportUserTypes.includes(user.type)
    ) {
      result = false; // Disallow user types that are not supported by the route
      setRedirectUrl('/signin');
      setRedirectBackAfterAuth(false);
      console.log(`Route ${rest.path} does not support user type ${user.type}`);
    } else if (
      !rest.supportsNonPremium &&
      !userAuthorizationInfo.canAccessStudentUI &&
      user.type === UserType.Student
    ) {
      result = false; // Disallow non-premium users
      setRedirectUrl('/unlock-access');
      console.log(`Route ${rest.path} does not support non-premium users`);
    } else {
      result = !!user; // Allow authenticated users
    }
    setAllow(result);
    setIsLoading(false);
  }, [
    rest.supportsAnonymous,
    rest.supportsNonPremium,
    allowAnonymous,
    isAuthenticated,
    isUserAnonymous,
    user,
    userAuthorizationInfo,
  ]);

  return (
    <Route
      {...rest}
      render={(props) =>
        isLoading ? (
          <div
            className="flex-1 flex justify-center items-center"
            style={{ height: 'calc(100dvh - 60px)' }}
          >
            <LoadingIcon />
          </div>
        ) : allow ? (
          <div className={'Protected flex-1'}>
            <Component {...props} />
          </div>
        ) : (
          <Redirect
            to={{
              pathname: redirectUrl,
              state: {
                redirectUrl: redirectBackAfterAuth
                  ? `${props.location.pathname}${props.location.search}${props.location.hash}`
                  : undefined,
              },
            }}
          />
        )
      }
    />
  );
};

export default ProtectedRoute;

export const useNavigate = () => {
  let history = useHistory();

  return (path: string, state?: any) => {
    history.push(path, state);
  };
};
